import { Context } from "@nuxt/types"

import { NAVIGATION_TYPE } from '~/constants/analytics'
import getScreenTypeByRoute from '~/helpers/get-screen-type-by-route'
import { getRouteName } from "~/utils/getRouteName"
import { getMembershipTier } from '~/utils/getMembershipTier'

import window from "./window"

export type YesNo = "yes" | "no"

export enum UserStatus {
  LoggedIn = "logged-in",
  LoggedOut = "logged-out"
}

export type AnalyticsCustomer = {
  id: string;
  membershipType: string;
  membershipTier: string;
} | null

export type AnalyticsUser = {
  loginStatus: UserStatus,
  customer?: AnalyticsCustomer
}

export type AnalyticsError = {
  id: string;
  code: string;
  message: string;
}

export type AnalyticsModule = {
  name: string;
  type: string;
  items: AnalyticsModuleItem[];
}

export type AnalyticsModuleItem = {
  id: string;
  position: string;
}

export type AnalyticsAssets = {
  [type: string]: { id: string }[]
}

export type ScreenEvent = {
  name: string;
  modules?: AnalyticsModule[];
  assets?: AnalyticsAssets;
}

export type AnalyticsPlatform = {
  regionalDomainPrefix: string;
}

export type AnalyticsEvent = ScreenEvent | {}

export type AnalyticsTypeValue = {
  type: string;
  value: string | object;
}

export type AnalyticsLayer = {
  events: AnalyticsEvent[];
  screen: { type: string };
  assets: AnalyticsAssets;
  modules: AnalyticsModule[];
  user?: AnalyticsUser;
  platform?: AnalyticsPlatform;
}

export default class Analytics {
  sohohouse: AnalyticsLayer = {
    screen: { type: '' },
    events: [],
    assets: {},
    modules: []
  }

  constructor (protected ctx: Context) {
    this.ctx = ctx

    if (process.server) this.setNuxtState()

    this.initialize(getRouteName(this.ctx.route))
    this.enableRouteCapture()
  }

  /**
   * ************* COMPUTED *************
   */

  /**
   * Reactive user from the vuex store
   *
   * @readonly
   * @private
   * @memberof Analytics
   */
  private get loginStatus (): UserStatus {
    return this.ctx.store.getters.currentUser ? UserStatus.LoggedIn : UserStatus.LoggedOut
  }

  /**
   * Reactive user object for analytics events
   * - Standardized user information format
   *
   * @readonly
   * @private
   * @memberof Analytics
   */
  private get user (): AnalyticsUser {
    let membershipType = ''
    const membershipTier = getMembershipTier(this.ctx.store.getters.currentUser)

    if (this.ctx.store.getters.currentUser) {
      const { membership } = this.ctx.store.getters.currentUser

      if (membership.nonMemberAccount) {
        membershipType = 'non-member'
      } else {
        // Will have the form of something like u27-local
        membershipType = `${membership.membershipType}-${membership.subscriptionType}`.toLowerCase()
      }
    }

    const customer: AnalyticsCustomer = this.ctx.store.getters.currentUser && {
      membershipType,
      membershipTier,
      id: this.ctx.store.getters.currentUser.id
    }
    return {
      loginStatus: this.loginStatus,
      ...(customer && { customer })
    }
  }

  /**
   * Domain specific details
   *
   * @readonly
   * @private
   * @memberof Analytics
   */
  private get platform (): AnalyticsPlatform {
    return {
      // @ts-ignore - This does exist on nuxt-i18n :/
      regionalDomainPrefix: this.ctx.app.i18n.getLocaleCookie()
    }
  }

  /**
   * Hydrate the server state from nuxt
   *
   * @private
   * @returns
   * @memberof Analytics
   */
  private hydrateCtx() {
    return this.ctx.nuxtState !== undefined ? this.ctx.nuxtState.sohohouse : {}
  }

  /**
   * Send the server state to nuxt
   *
   * @private
   * @memberof Analytics
   */
  private setNuxtState () {
    this.ctx.beforeNuxtRender(({ nuxtState }) => {
      nuxtState.sohohouse = this.sohohouse
    })
  }

  /**
   * Capture a screen event on route change (client only)
   *
   * @private
   * @memberof Analytics
   */
  private enableRouteCapture() {
    if (this.ctx.app.router && process.client) {
      // Capture screen events in all nuxt routes
      this.ctx.app.router.afterEach((to, from) => {
        if (!to || !to.name) return
        if (to.path === from.path && to.path !== '/') return
        const name = getRouteName(to)
        this.screen({ name, ...(to.meta.analytics || {}) })
      })
    }
  }

  /**
   * ************* METHODS *************
   */

  /**
   * Create the analytics dataLayer instance on construction
   *
   * @param {string} routeName
   * @memberof Analytics
   */
  initialize(routeName: string) {
    const ssrState = this.hydrateCtx()
    this.sohohouse = window.sohohouse = {
      assets: { ...ssrState.assets || {} },
      modules: [ ...ssrState.modules || [] ],
      events: [ ...ssrState.events || [] ],
      screen: { type: routeName },
      user: this.user,
      platform: this.platform,
    }
  }

  /**
   * Capture the event
   */
  push (...args: Object[]) {
    this.sohohouse.events.push(...args)
  }

  /**
   * Sends an event after user login
   */
  login () {
    this.push({
      event: "user.interact.auto.success.loginSuccess",
      user: this.user
    })
  }

  /**
   * Sends an event after user logout
   */
  logout () {
    this.push({
      event: "user.interact.auto.success.logoutSuccess",
      user: {
        loginStatus: UserStatus.LoggedOut
      }
    })
  }

  /**
   * Sends the screen
   */
  screen ({ name: type, modules, assets }: ScreenEvent) {
    this.push({
      event: "screen.load.auto.dataLayer.load",
      screen: { type },
      ...(modules && { modules }),
      ...(assets && { assets })
    })
  }

  /**
   * Sends an event after application submission
   */
  submitApplication () {
    this.push({
      event: 'user.interact.auto.success.applicationSucccess',
      forms: {
        id: 'membership-application'
      },
      user: this.user
    })
  }

  addModule(moduleOrModules: AnalyticsModule | AnalyticsModule[]) {
    const modules = Array.isArray(moduleOrModules) ? moduleOrModules : [moduleOrModules]

    this.sohohouse.modules = [
      ...this.sohohouse.modules,
      ...modules,
    ]
  }

  addAssets(type: string, assets: { id: string }[]) {
    const currentAssetsByType: { id: string }[] = this.sohohouse.assets[type] || []
    this.sohohouse.assets[type] = [
      ...currentAssetsByType,
      ...assets.filter(({ id }) => currentAssetsByType.find(c => c.id !== id) || true),
    ]
  }

  /**
   * Sends an error to help describe user journey
   */
  error (error: AnalyticsError) {
    this.push(error)
  }

  /**
   * Generate a module item list
   *
   * @static
   * @param {{ id: string }[]} modules
   * @returns {AnalyticsModuleItem[]}
   * @memberof Analytics
   */
  generateModuleItems(modules: { id: string }[], type: string): AnalyticsModuleItem[] {
    return modules.map(({ id }, i) => ({ id, type, position: (i + 1).toString() }))
  }

  /**
   * Generate an array of type/value objects from an object
   *
   * @param {({ [key: string]: object | string[] | string })} items
   * @returns {AnalyticsTypeValue[]}
   * @memberof Analytics
   */
  generateTypeValues(items: { [key: string]: object | string[] | string }): AnalyticsTypeValue[] {
    return Object.entries(items).reduce((filters: AnalyticsTypeValue[], [type, value]) => {
      if (Array.isArray(value)) {
        return [
          ...filters,
          ...value.map(arrayItemValue => ({ type, value: arrayItemValue }))
        ]
      }

      return [
        ...filters,
        { type, value }
      ]
    }, [])
  }

  /**
   * Returns the navigation type based on navigation
   *
   * @param {{ string }} route
   * @returns {Object}
   * @memberof Analytics
   */
  generateNavigationAnalytics = (route: ScRoute, positions: number[] = []) => {
    if (
      route.component === NAVIGATION_TYPE.NAVIGATION_DROPDOWN ||
      !route.link.value.href
    ) return {}

    // TODO: include position once we can determine it
    return {
      name: getScreenTypeByRoute(route.link.value.href),
      type: NAVIGATION_TYPE.NAVIGATION_MENU,
      position: positions.join('.')
    }
  }
}
